import { useQuery } from '@tanstack/react-query';
import { fetcher } from '@/shared/api';
import { superCategoryKeys } from './queryKeys';
import { unwrapSuperCategory } from '../model/types';
import type { SystemSuperCategory } from '../model/types';

/**
 * By default this mirrors the endpoint's own default: a single page. Pass
 * `{ paginate: false }` to fetch the full collection instead (the
 * documented `paginate=false` override) — needed by any screen, like the
 * provider category-mapping tab, whose job is to guarantee nothing is left
 * unmapped. The bare call is left byte-for-byte unchanged so its four
 * existing callers are unaffected.
 */
export function useSystemSuperCategoryList(params?: { paginate?: boolean }) {
  return useQuery({
    queryKey: superCategoryKeys.lists(params),
    queryFn: () =>
      fetcher(
        `/category-variant/super-categories${params?.paginate === false ? '?paginate=false' : ''}`,
      ),
  });
}

/** The subtree under one super-category. Returned raw so callers can read it
 *  through `childrenOfTree`, which handles both documented-by-nobody shapes the
 *  endpoint might use. */
export function useSystemSuperCategoryTree(id?: string) {
  return useQuery({
    queryKey: superCategoryKeys.tree(id ?? ''),
    queryFn: () => fetcher(`/category-variant/super-categories/${id}/tree`),
    enabled: !!id,
  });
}

/**
 * One super-category by id.
 *
 * The envelope is unwrapped here rather than at each call site — the previous
 * `(data as any)?.data ?? data` cast at two of them is what made the pages carry
 * `any` all the way through their render.
 *
 * Resolving a parent's NAME goes through this hook with the parent's id, never
 * through a `.find()` over the list: the list is paginated server-side, so a
 * scan silently misses any parent past the first page.
 */
export function useSystemSuperCategoryDetail(id?: string) {
  return useQuery<SystemSuperCategory>({
    queryKey: superCategoryKeys.detail(id ?? ''),
    queryFn: async () =>
      unwrapSuperCategory(
        await fetcher<unknown>(`/category-variant/super-categories/${id}`),
      ) ?? {},
    enabled: !!id,
  });
}
